import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import phoneFormatter from 'helpers/phone';
import { Phone, Copy, Check } from 'react-feather';

export default function Contact({ data }) {
  const [copied, setCopied] = useState(false);
  const formatted = phoneFormatter(data);

  function handleCopy() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(data);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleCall() {
    window.open(`tel:${data}`);
  }

  return (
    <Container>
      <HeroSection>
        <IconRing>
          <IconCircle>
            <Phone size={22} color={colors.neutralLight} />
          </IconCircle>
        </IconRing>
        <Title>Telefone de Contato</Title>
        <Subtitle>Ligue diretamente ou copie o numero</Subtitle>
      </HeroSection>

      <PhoneCard>
        <PhoneNumber>{formatted}</PhoneNumber>
      </PhoneCard>

      <ActionsRow>
        <ActionButton onClick={handleCall} primary>
          <Phone size={15} />
          <span>Ligar</span>
        </ActionButton>
        <ActionButton onClick={handleCopy} copied={copied}>
          {copied ? <Check size={15} /> : <Copy size={15} />}
          <span>{copied ? 'Copiado!' : 'Copiar'}</span>
        </ActionButton>
      </ActionsRow>
    </Container>
  );
}

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Container = styled(Column)`
  width: 100%;
  align-items: center;
  gap: 0;
`;

const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const IconRing = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(79, 134, 247, 0.08);
  margin-bottom: 4px;
  animation: ${scaleIn} 0.4s ease forwards;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary});
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(79, 134, 247, 0.3);
`;

const Title = styled.h3`
  font-size: ${fonts.sizes.highlight};
  font-weight: ${fonts.weight.bold};
  color: ${colors.nightDark};
  margin: 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
  margin: 0;
  text-align: center;
`;

const PhoneCard = styled.div`
  width: 100%;
  padding: 16px 20px;
  background: ${colors.neutral};
  border-radius: 12px;
  text-align: center;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

const PhoneNumber = styled.span`
  font-size: ${fonts.sizes.title};
  font-weight: ${fonts.weight.bold};
  color: ${colors.nightDark};
  letter-spacing: 1px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: ${fonts.sizes.default};
  font-weight: ${fonts.weight.bold};
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;

  ${({ primary }) => primary ? `
    background: linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary});
    color: ${colors.neutralLight};
    box-shadow: 0 3px 10px rgba(63, 107, 198, 0.25);

    &:hover {
      box-shadow: 0 5px 16px rgba(63, 107, 198, 0.35);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba(63, 107, 198, 0.2);
    }
  ` : `
    background: ${colors.neutral};
    color: ${colors.nightDark};
    border: 1px solid rgba(0, 0, 0, 0.08);

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }

    &:active {
      background: rgba(0, 0, 0, 0.06);
    }
  `}

  ${({ copied }) => copied && `
    background: rgba(43, 200, 102, 0.1) !important;
    color: ${colors.successDark} !important;
    border-color: rgba(43, 200, 102, 0.2) !important;
  `}

  & > svg {
    flex-shrink: 0;
  }
`;
